<template>
	<div :id="uid" class="global-forms-card">
		<div class="global-forms-card__status">
			<NotificationIcon
				:variant="notificationVariantIcon"
				ref="tooltipStatusRef"
				@mouseenter="tooltipStatusVisible = true"
				@focus="tooltipStatusVisible = true"
				@mouseleave="tooltipStatusVisible = false"
				@blur="tooltipStatusVisible = false"
			/>
			<Tooltip
				:text="formStatus"
				:trackedRefName="'tooltipStatusRef'"
				:display="tooltipStatusVisible"
				:offset="[4, 4]"
				position="top-start"
			/>
		</div>
		<div class="global-forms-card__body">
			<div class="global-forms-card__main">
				<p class="global-forms-card__title">
					{{ form.id }}
					<span class="global-forms-card__divider"> | </span>
					<Ellipsis :data="form.name"></Ellipsis>
				</p>

				<!-- Per Product/Forms Team - DO NOT truncate Revision; need to see end -->
				<p class="global-forms-card__revision">
					{{ form.formRevision }}
				</p>
			</div>
			<div class="global-forms-card__type">
				{{ globalFormPrintType }}
			</div>
			<div class="global-forms-card__full-container">
				<Ellipsis
					:data="form.description"
					class="global-forms-card__description"
				></Ellipsis>

				<span class="global-forms-card__sync">
					{{ globalFormStatus }}
				</span>
			</div>
		</div>
		<div class="global-forms-card__footer">
			<ActionsSet
				class="global-forms-card__actions"
				:id="`${uid}_action`"
				:variant="variant"
				v-model="optionsData"
			/>
		</div>
	</div>
</template>

<script>
import ActionsSet from '@/site-components/actions-set/actions-set';
import NotificationIcon from '@/components/notifications/notification-icon/notification-icon';
import Tooltip from '@/components/shells/tooltip/tooltip';
import PdfFnForm from '@/mixins/forms/pdf-fn-form';
import RenderActionCard from '@/mixins/render-action-card';
import Ellipsis from '@/components/visual/ellipsis';
import _ from 'lodash';

export default {
	name: 'GlobalFormsCard',
	mixins: [PdfFnForm, RenderActionCard],
	components: {
		ActionsSet,
		NotificationIcon,
		Tooltip,
		Ellipsis
	},
	data() {
		return {
			tooltipStatusVisible: false,
			tooltipTitleVisible: false,
			tooltipCodeVisible: false
		};
	},
	props: {
		form: {
			type: Object,
			default: () => {}
		}
	},

	computed: {
		hasFormCreatePermission() {
			return this.validatePermission('forms.create');
		},
		hasFormUpdatePermission() {
			return this.validatePermission('forms.update');
		},
		globalFormStatus() {
			return this.form.status === 'U'
				? 'Unpublished'
				: this.form.status === 'P'
				? 'Pending Changes'
				: '';
		},
		globalFormPrintType() {
			return this.form.formPrintType === 'L' ? 'Laser' : 'Impact';
		},
		showPdfIcone() {
			return this.form.formPrintType === 'L';
		},
		disabledPdfIcone() {
			return this.showPdfIcone && !this.form.pdfData;
		},
		formStatus() {
			if (!this.form.active) {
				return 'Inactive';
			}
			return 'Active';
		},
		notificationVariantIcon() {
			if (!this.form.active) {
				return 'inactive';
			}
			return 'positive';
		},
		formPrintTypeLC() {
			return (this.globalFormPrintType || '').toLowerCase();
		},
		customerPath() {
			if (_.startsWith(this.$route.path, '/clients/')) {
				return this.$route.path
					.split('/')
					.slice(0, 3)
					.join('/');
			}

			return '';
		},
		cardTitleTooltipText() {
			return `${this.form.id} | ${this.form.name}`;
		}
	},
	methods: {
		generateActions() {
			let items = [];
			items.push({
				id: 1,
				action: this.manageCustomers,
				disabled: !this.hasFormUpdatePermission,
				iconClass: 'icon icon-company',
				label: 'Clients',
				title:
					_.get(this, 'form.customers.length', 0) > 0
						? 'Select to see the clients using this form'
						: 'This form has no clients assigned',
				type: 'anchor',
				visible: true,
				valueBadgeCount: this.form.customers.length
			});
			if (this.hasFormUpdatePermission) {
				items.push({
					id: items.length + 1,
					action: {
						path: `${this.customerPath}/forms/edit/${this.form.id}/${this.formPrintTypeLC}`
					},
					iconClass: 'icon icon-edit',
					label: 'Edit',
					type: 'route-link',
					title: 'Select to edit this form',
					visible: true
				});
			} else {
				items.push({
					id: items.length + 1,
					action: {
						path: `${this.customerPath}/forms/preview/${this.form.id}/${this.formPrintTypeLC}`
					},
					iconClass: 'icon icon-show',
					label: 'View',
					type: 'route-link',
					title: 'Select to view this form',
					visible: true
				});
			}

			if (this.form.formPrintType === 'L') {
				items.push({
					id: items.length + 1,
					action: this.getFile,
					disabled: this.disabledPdfIcone,
					title: this.disabledPdfIcone
						? "This form doesn't have pdf assigned."
						: 'Select to preview this form',
					iconClass: 'icon icon-show',
					label: 'PDF',
					type: 'anchor',
					visible: true
				});
			}
			if (this.hasFormCreatePermission) {
				items.push({
					id: items.length + 1,
					action: {
						path: `${this.customerPath}/forms/copy/${this.form.id}/${this.formPrintTypeLC}`
					},
					disabled: false,
					iconClass: 'icon icon-copy',
					label: 'Copy Of',
					title: 'Select to create a copy of this form',
					type: 'route-link',
					visible: true
				});
			}
			items.push({
				id: items.length + 1,
				action: {
					path: `/forms/${this.form.id}/history`,
					params: {
						changedEntityId: this.form.id
					}
				},
				disabled: false,
				iconClass: 'icon icon-history',
				label: 'History',
				title: `Select to go to the history page of "${this.form.name}"`,
				type: 'route-link',
				visible: true
			});

			return items;
		},
		manageCustomers() {
			this.$emit('click', { ...this.form });
		}
	},
	watch: {
		form: {
			immediate: true,
			deep: true,
			handler() {
				this.actions = this.generateActions();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Global Forms Card
		* Can use SCSS variables = TRUE
    *
 */

@import '@/scss/5-objects/_cards.scss';
@import '@/scss/5-objects/_form.scss';

.global-forms-card {
	@include card;
	display: grid;
	grid-template-columns: auto 1fr;
	column-gap: $gutter-lg;
	row-gap: $gutter-md;
	align-items: start;
	font-size: $default-font-size - 2;

	&__status {
		position: relative;
		@include vertical-horizontal-center-with-flex;
		z-index: 1;
		cursor: default;
	}

	&__body {
		display: grid;
		grid-template-columns: 1fr;
		gap: $gutter-md;

		@include mq($from: lg) {
			grid-template-columns: 3fr 1fr;
		}
	}

	&__main {
		min-width: 100%;
	}

	&__title {
		width: 100%;
		@include heading('md');
		margin-bottom: $gutter-sm;
		display: inline-flex;
		flex-direction: row;
	}

	&__divider {
		color: $neutral-3;
		line-height: 1.25;
		margin: 0 $gutter-sm;
	}

	&__type {
		display: flex;
		align-items: center;
		align-self: flex-start;
		font-weight: $medium-font-weight;
		color: $neutral-1;

		@include mq($from: lg) {
			grid-column: 2/3;
			justify-content: flex-end;
		}
	}

	&__full-container {
		display: grid;
		gap: $gutter-md;

		@include mq($from: lg) {
			grid-column: 1/3;
		}
	}

	&__description {
		font-size: $default-font-size - 3;
		line-height: 16px;
	}

	&__sync {
		cursor: default;
		text-transform: uppercase;
		font-weight: $medium-font-weight;
		@include text-contrast($page-body-bkgd, $white, $neutral-2);
	}

	&__footer {
		grid-column: 2/3;
		align-self: flex-end;
		display: flex;
		flex-direction: column;
	}
}
</style>
